import React, { useState } from 'react';
import './anchor-styles.css';

/**
 * Supplier Management Component
 */
const SupplierManagement = ({ suppliers = [], loading, onViewSupplierInvoices }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.gst?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="supplier-management-loading">
        <div className="text-center" style={{ padding: '50px' }}>
          <i className="fa fa-spinner fa-spin fa-3x"></i>
          <p style={{ marginTop: '15px' }}>Loading suppliers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="supplier-management">
      <div className="supplier-header">
        <h4 style={{ marginBottom: '20px', fontWeight: '600' }}>
          <i className="fa fa-building"></i> My Suppliers
        </h4>
        
        {/* Search Bar */}
        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-addon">
                <i className="fa fa-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by Name or GST..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-6 text-right">
            <button className="btn btn-primary">
              <i className="fa fa-plus"></i> Add Supplier
            </button>
          </div>
        </div>
      </div>

      {/* Supplier List */}
      {filteredSuppliers.length === 0 ? (
        <div className="text-center" style={{ padding: '50px' }}>
          <i className="fa fa-inbox fa-3x" style={{ color: '#ccc' }}></i>
          <h4 style={{ marginTop: '15px', color: '#999' }}>No Suppliers Found</h4>
          <p style={{ color: '#999' }}>Try adjusting your search</p>
        </div>
      ) : (
        <div className="supplier-list">
          {filteredSuppliers.map((supplier) => (
            <div key={supplier.id} className="supplier-card">
              <div className="supplier-card-header">
                <div className="supplier-logo-circle">
                  {supplier.name?.charAt(0).toUpperCase()}
                </div>
                <div className="supplier-name-info">
                  <h5>{supplier.name}</h5>
                  <span className="supplier-gst">GST: {supplier.gst}</span>
                  <span 
                    className={`supplier-status ${supplier.status === 'Active' ? 'active' : 'inactive'}`}
                  >
                    {supplier.status}
                  </span>
                </div>
                <div className="supplier-actions">
                  <div className="dropdown">
                    <button 
                      className="btn btn-default btn-xs dropdown-toggle" 
                      type="button" 
                      data-toggle="dropdown"
                    >
                      <i className="fa fa-ellipsis-v"></i>
                    </button>
                    <ul className="dropdown-menu dropdown-menu-right">
                      <li>
                        <a href="#" onClick={(e) => {
                          e.preventDefault();
                          onViewSupplierInvoices(supplier.id);
                        }}>
                          <i className="fa fa-file-text-o"></i> View All Invoices
                        </a>
                      </li>
                      <li>
                        <a href="#">
                          <i className="fa fa-info-circle"></i> View Details
                        </a>
                      </li>
                      <li>
                        <a href="#">
                          <i className="fa fa-clock-o"></i> Set Payment Terms
                        </a>
                      </li>
                      <li className="divider"></li>
                      <li>
                        <a href="#">
                          <i className="fa fa-download"></i> Download Statement
                        </a>
                      </li>
                      <li className="divider"></li>
                      <li>
                        <a href="#" style={{ color: '#F44336' }}>
                          <i className="fa fa-ban"></i> Block Supplier
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="supplier-card-body">
                <div className="supplier-stats">
                  <div className="stat-item">
                    <div className="stat-label">Total Invoices</div>
                    <div className="stat-value">{supplier.totalInvoices || 0}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">Approved</div>
                    <div className="stat-value" style={{ color: '#4CAF50' }}>
                      {supplier.approvedInvoices || 0}
                    </div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">Avg Amount</div>
                    <div className="stat-value">
                      ₹{formatAmount(supplier.avgAmount || 0)}
                    </div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">Total Financed</div>
                    <div className="stat-value" style={{ color: '#1976D2' }}>
                      ₹{formatAmount(supplier.totalFinanced || 0)}
                    </div>
                  </div>
                </div>

                {supplier.contact && (
                  <div className="supplier-contact" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #f0f0f0' }}>
                    <div className="row">
                      <div className="col-md-6">
                        <small className="text-muted">
                          <i className="fa fa-user"></i> {supplier.contact?.name || 'N/A'}
                        </small>
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted">
                          <i className="fa fa-phone"></i> {supplier.contact?.phone || 'N/A'}
                        </small>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="supplier-card-footer">
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => onViewSupplierInvoices(supplier.id)}
                >
                  <i className="fa fa-eye"></i> View Invoices
                </button>
                <button className="btn btn-default btn-sm" style={{ marginLeft: '10px' }}>
                  <i className="fa fa-commenting"></i> Message
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function
const formatAmount = (amount) => {
  if (amount >= 10000000) {
    return `${(amount / 10000000).toFixed(2)} Cr`;
  } else if (amount >= 100000) {
    return `${(amount / 100000).toFixed(2)} L`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(2)} K`;
  }
  return amount?.toLocaleString('en-IN') || '0';
};

export default SupplierManagement;

